import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Col, Space, Typography, message } from 'antd';
import { useHistory } from 'react-router-dom';
import StatusTag from '../Campaigns/shared/StatusTag';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { getCampaignOrderErrorMessage } from '../../../core/utils/constants/campaignOrderErrorType';
import './styles.css';

const { Text } = Typography;

const CampaignRequestorReviewSummary = ({
  formValues,
  currentCampaignOrder,
  editable = false
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);
  const {
    updateCampaignOrder,
    selectedAdvertiser,
  } = useCampaignRequestorContext();

  // Cleanup function to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const campaignName = formValues?.name || '';
  const advertiser = formValues?.advertiser || currentCampaignOrder?.advertiser || selectedAdvertiser;

  const handleMakeEdits = () => {
    if (currentCampaignOrder?.id) {
      history.push(`/campaigns/requester/edit/${currentCampaignOrder.id}`);
    }
  };

  const handleSave = async () => {
    // Only save if we have an existing campaign order (review mode should always have one)
    if (!currentCampaignOrder?.id) {
      message.warning('No campaign order to save');
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      message.loading('Saving budget changes...', 0);

      const campaigns = formValues.campaigns || [];
      const existingCampaign = currentCampaignOrder?.campaigns?.[0];

      // Only update if we have campaigns and line items to update
      if (!existingCampaign?.id || campaigns.length === 0) {
        message.destroy();
        message.info('No changes to save');
        return;
      }

      // Build minimal update payload - only update line item budgets
      const updateData = {
        campaigns: {
          update: {
            where: { id: existingCampaign.id },
            data: {
              lineItems: {
                update: campaigns
                  .filter(lineItem => lineItem.id) // Only update existing line items
                  .map(lineItem => ({
                    where: { id: lineItem.id },
                    data: {
                      budgetTotal: Number(lineItem.budgetTotal || 0),
                      // Only update budget-related fields
                    }
                  }))
              }
            }
          }
        }
      };

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: updateData,
        },
      });

      message.destroy();
      message.success('Budget changes saved successfully');
    } catch (error) {
      message.destroy();
      const errorMessages = getCampaignOrderErrorMessage(error);
      message.error(errorMessages[0] || 'Failed to save changes', 5);
      console.error('Save error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmitRequest = async () => {
    if (!currentCampaignOrder?.id) {
      message.warning('No campaign order to submit');
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setLoading(true);

      // Save any pending changes first
      await handleSave();

      message.loading('Submitting request...', 0);

      // Determine the target status based on current status
      const status = currentCampaignOrder?.status === 'LIVE' ? 'LIVE_PENDING' : 'PENDING';

      // Build minimal update payload to change status only
      const updateData = {
        status,
      };

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: updateData,
        },
      });

      message.destroy();
      message.success('Request submitted successfully');

      // Navigate to campaigns main page
      history.push('/campaigns/main/1');
    } catch (error) {
      message.destroy();
      const errorMessages = getCampaignOrderErrorMessage(error);
      message.error(errorMessages[0] || 'Failed to submit request', 5);
      console.error('Submit request error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Col className="campaign-requester-sticky">
      <Card
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: advertiser ? 4 : 0,
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              SUMMARY {campaignName ? <small>- [{campaignName}] </small> : ''}
            </span>
            {currentCampaignOrder && (
              <StatusTag
                startDate={currentCampaignOrder.startDate || new Date().toString()}
                endDate={currentCampaignOrder.endDate || new Date().toString()}
                statusCampaign={currentCampaignOrder.status}
                attributionWindow={currentCampaignOrder.attributionWindow || 7}
              />
            )}
          </div>
        }
        style={{ marginBottom: 20 }}
        className="campaign-requester-summary"
      >
        {advertiser && (
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #f0f0f0' }}>
            <Text>{advertiser.name}</Text>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <Text>
            Take a moment to review the details of this request before submitting it.
          </Text>
        </div>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Button
            type="default"
            block
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            Save
          </Button>

          <Button
            type="default"
            block
            onClick={handleMakeEdits}
            disabled={loading}
          >
            Make Edits
          </Button>

          <Button
            type="primary"
            block
            onClick={handleSubmitRequest}
            disabled={loading}
          >
            Submit Request
          </Button>
        </Space>
      </Card>
    </Col>
  );
};

export default CampaignRequestorReviewSummary;